import { useEffect } from 'react';
import { AccessibleStatus, AccessibleWarningStatus, Close, ClusteredStatus, CompleteStatus, CompleteWithWarningsStatus, ConditionalCompleteStatus, ConditionalStatus, DebugStatus, DisconnectedStatus, EmbeddedStatus, ErrorStatus, FlaggedStatus, HiddenStatus, HistoricalStatus, ImportantStatus, IncompleteInProgressStatus, IncompleteStatus, IncompleteWarningStatus, InformationStatus, InsightFoundStatus, InsightStatus, InvalidStatus, LowPriorityStatus, MediumPriorityStatus, MergedStatus, NewStatus, OnDemandStatus, OutOfSyncStatus, PausedStatus, ProgressStatus, RequiredStatus, RevertedStatus, ReviewStatus, ScheduledStatus, StoppedStatus, StoppingStatus, SubmittedStatus, VisibleStatus, WarningStatus } from '@sas/react-svg';
import cx from 'classnames';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { getNextFocusableElement } from '../../util/focus';
import { Button, ButtonVariant } from '../Button';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { HelpPopup } from '../HelpPopup';
import { Icon } from '../Icon';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { Link } from '../Link';
import { MenuButton } from '../MenuButton';
import { Text } from '../Text';
import { DefaultIconNames, ErrorIconNames, InformationalIconNames, SuccessIconNames, WarningIconNames } from './types';
import bundle from './NovaComponents-MessageStrip-gui-icu.properties';
import cn from './MessageStrip.css.json';
import './MessageStrip.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const iconMap = {
  accessible: AccessibleStatus,
  accessibleWarning: AccessibleWarningStatus,
  clustered: ClusteredStatus,
  complete: CompleteStatus,
  completeWithWarnings: CompleteWithWarningsStatus,
  conditionalComplete: ConditionalCompleteStatus,
  conditional: ConditionalStatus,
  debug: DebugStatus,
  disconnected: DisconnectedStatus,
  embedded: EmbeddedStatus,
  error: ErrorStatus,
  flagged: FlaggedStatus,
  hidden: HiddenStatus,
  historical: HistoricalStatus,
  important: ImportantStatus,
  incompleteInProgress: IncompleteInProgressStatus,
  incomplete: IncompleteStatus,
  incompleteWarning: IncompleteWarningStatus,
  information: InformationStatus,
  informational: InformationStatus,
  insightFound: InsightFoundStatus,
  insight: InsightStatus,
  invalid: InvalidStatus,
  lowPriority: LowPriorityStatus,
  mediumPriority: MediumPriorityStatus,
  merged: MergedStatus,
  new: NewStatus,
  onDemand: OnDemandStatus,
  outOfSync: OutOfSyncStatus,
  paused: PausedStatus,
  progress: ProgressStatus,
  required: RequiredStatus,
  reverted: RevertedStatus,
  review: ReviewStatus,
  scheduled: ScheduledStatus,
  stopped: StoppedStatus,
  stopping: StoppingStatus,
  submitted: SubmittedStatus,
  success: CompleteStatus,
  visible: VisibleStatus,
  warning: WarningStatus
};
export const MessageStrip = props => {
  const dt = useDesignTokens();

  // line-height patch specific to message strip to help keep message strip's icon and its label centered
  // NOTE: this will break if the unit is not px
  const labelFontSizeInPixels = parseFloat(dt.enableNovaTokens ? dt.tokens['font-size-body-text-m'] : dt.reportTokens.font.size.m) * 16;
  const labelHeight = Math.round(labelFontSizeInPixels * Number(dt.enableNovaTokens ? dt.tokens['font-space-line-height'] : dt.reportTokens.font.lineHeight));
  const type =
  // If a type was provided, use it
  props.type ?? (
  // If an icon was also not provided, default to informational
  props.icon === undefined ? 'informational' :
  // Otherwise choose the message strip type that matches what the chosen icon should be used on
  ErrorIconNames.includes(props.icon) ? 'error' : WarningIconNames.includes(props.icon) ? 'warning' : InformationalIconNames.includes(props.icon) ? 'informational' : SuccessIconNames.includes(props.icon) ? 'success' :
  // Fallback in case our icon isn't in one of our lists
  'informational');

  // If someone didn't set a specific icon, just match the type
  const icon = props.icon ?? type;

  // the 14 is the height of the message strip's icon in pixels.
  const marginTopCalc = `${Math.round((labelHeight - 14) / 2)}px`;
  const tokens = {
    ...getMessageStripDesignTokens(dt, {
      type,
      tokens: props.tokens
    }),
    ...props.theme?.(dt)
  };
  const localize = useTextLocalizer();
  const getIconA11yInfo = useIconA11yInfo();
  const tooltip =
  // If a custom tooltip was provided, use it
  props.tooltip ?? (
  // Check if this is one of our default icons
  DefaultIconNames.includes(icon) ?
  // if so we have a custom localized text to use
  localize(`${icon}.label`) :
  // Otherwise use the tooltip built into the icon
  getIconA11yInfo({
    icon: iconMap[icon]
  }).tooltip);
  const ariaLabel = localize(`container.aria`, {
    messageType: tooltip ?? '',
    messageText: props.children ?? ''
  });
  const directSpeech = useDirectSpeech();
  useEffect(() => {
    if (!props.disableDirectSpeech) {
      directSpeech?.notify(ariaLabel);
    }
  }, [directSpeech, localize, ariaLabel, props.disableDirectSpeech]);
  return /*#__PURE__*/_jsx("div", {
    className: cx(cn.container, props.className),
    style: {
      ...tokens,
      ...props.style
    },
    role: "group",
    children: /*#__PURE__*/_jsxs("div", {
      className: cn.sectionMessage,
      "data-testid": props.testId,
      tabIndex: 0,
      "aria-label": ariaLabel,
      children: [/*#__PURE__*/_jsx(Icon, {
        icon: iconMap[icon],
        tooltip: tooltip,
        className: cn.icon,
        role: "img",
        ariaHidden: true,
        style: {
          marginTop: marginTopCalc
        }
      }), /*#__PURE__*/_jsxs("div", {
        className: cn.contentContainer,
        children: [/*#__PURE__*/_jsx(Text, {
          className: cn.text,
          wrapText: !props.suppressWrapText,
          tokens: () => ({
            fg: 'var(--message-toast-fg)'
          }),
          children: props.children ?? ''
        }), props.linkProps ? /*#__PURE__*/_jsx(Link, {
          children: props.linkProps.text,
          href: props.linkProps.href,
          className: cn.link,
          small: true,
          onPress: props.linkProps.onPress
        }) : null, props.helpPopupProps ? /*#__PURE__*/_jsx(HelpPopup, {
          ...props.helpPopupProps,
          className: cn.helpButton
        }) : null]
      }), /*#__PURE__*/_jsx("div", {
        className: cn.spacer
      }), props.actionButtonProps ? props.actionButtonProps.type === 'MenuButton' ? /*#__PURE__*/_jsx("div", {
        className: cn.menuButtonContainer,
        children: /*#__PURE__*/_jsx(MenuButton, {
          variant: ButtonVariant.Small,
          ...props.actionButtonProps,
          testId: props.testId
        })
      }) : /*#__PURE__*/_jsx(Button, {
        variant: ButtonVariant.Small,
        className: cn.action,
        ...props.actionButtonProps,
        type: undefined,
        onPress: props.actionButtonProps.onPress
      }) : null, props.onClose ? /*#__PURE__*/_jsx("div", {
        className: cn.close,
        children: /*#__PURE__*/_jsx(Button, {
          className: cn.closeButton,
          variant: ButtonVariant.SmallIcon,
          onPress: () => {
            // getting the next focusable element searches the whole DOM for focusable elements, which is potentially
            // (somewhat) expensive when the DOM is large.  To avoid unnecessary overhead when consumers are providing
            // their own refocus behavior, the check is done here rather than as part of the boolean below.
            const nextFocusable = props.suppressRefocusOnClose ? null : getNextFocusableElement();
            const shouldRefocus = document.activeElement !== nextFocusable;
            props.onClose();
            if (shouldRefocus) nextFocusable?.focus();
          },
          icon: {
            icon: Close,
            tooltip: props.closeAriaLabel ?? localize('close.button.aria')
          }
        })
      }) : null]
    })
  });
};
export const getMessageStripDesignTokens = (dt, opts) => {
  // For selecting our styles we want to always set 'alert' instead of 'error'.
  const correctedType = opts.type === 'error' ? 'alert' : opts.type === 'informational' ? 'info' : opts.type;
  return {
    ...renderDesignTokens({
      bg: dt.tokens[`color-status-background-${correctedType}`],
      bd: dt.tokens[`color-status-border-${correctedType}`],
      fg: dt.tokens[`color-status-text-${correctedType}`],
      pd: `${dt.tokens['space-base-xxs']} ${dt.tokens['space-base-xs']}`,
      'pd-none': dt.tokens['space-base-null'],
      'pd-xs': dt.tokens['space-base-xs'],
      'pd-s': dt.tokens['space-base-s'],
      'pd-m': dt.tokens['space-base-m'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'icon-fg': dt.tokens[`color-status-icon-${correctedType}`],
      'border-radius': dt.tokens['size-border-radius-xxs'],
      'help-button-size': dt.tokens['space-base-l'],
      'outline-offset': 'calc(-1 * var(--focus-width))',
      ...opts.tokens?.()
    }, 'message-strip'),
    ...(!dt.enableNovaTokens && getMessageStripLegacyDesignTokens(dt, opts))
  };
};
const getMessageStripLegacyDesignTokens = (dt, opts) => {
  const correctedType = opts.type === 'error' ? 'alert' : opts.type;
  return renderDesignTokens({
    ...dt.stateStatus[correctedType],
    'font-size-m': dt.reportTokens.font.size.m,
    'icon-fg': dt.stateStatus[correctedType].fg,
    ...opts.tokens?.()
  }, 'message-strip');
};
//# sourceMappingURL=MessageStrip.js.map